import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { queryClient } from '@/lib/queryClient';
import { performanceMonitor } from '@/services/performance-monitor';
import { logger } from '@/services/debug-logger';
import { useAuth } from '@/contexts/auth-context';
import { onTokenRefresh } from '@/lib/auth';
import { deltaManager } from '@/services/delta-manager';
import { audioNotifier } from '@/lib/audio-notifier';
import { chatNotify } from '@/lib/chat-notify';
import { missedCallToastsStore } from '@/lib/missed-call-toasts';
import { invalidateCalendar, invalidateTimeOffCalendarSources } from '@/lib/calendar-cache';
import { invalidateLinkedShareProjectionQueries } from '@/lib/linked-share-cache';
import { invalidateEmailWorkbenches } from '@/lib/email-workbench-cache';

/**
 * Refetch everything chat-related. Called after a WebSocket reconnect and
 * when the tab becomes visible again: broadcast-only chat messages have no
 * replay, so any connectivity gap can leave open chat windows stale until
 * we actively catch up (see task: reliable chat notifications).
 */
let lastChatCatchUpAt = 0;
const CHAT_CATCHUP_COOLDOWN_MS = 3000;

function catchUpChatQueries() {
  // Cooldown: rapid visibility flaps or reconnect loops must not turn into
  // an invalidation storm.
  const now = Date.now();
  if (now - lastChatCatchUpAt < CHAT_CATCHUP_COOLDOWN_MS) return;
  lastChatCatchUpAt = now;
  // Non-exact: also matches ['/api/chat/channels', <id>, 'messages'] etc.
  queryClient.invalidateQueries({ queryKey: ['/api/chat/channels'], exact: false });
  queryClient.invalidateQueries({ queryKey: ['/api/chat/channel-unread-counts'] });
  queryClient.invalidateQueries({ queryKey: ['/api/chat/unread-counts-all'] });
  queryClient.invalidateQueries({ queryKey: ['/api/notifications/count'] });
  // Task #168: the activity bar's new-call chime is driven by pushed
  // `call:activity` pokes — but those are broadcast-only (no replay). After
  // a socket gap or tab-hidden throttling, refetch immediately instead of
  // waiting out the 15s poll. The chime's seen-set baseline dedupes, so a
  // catch-up refetch can only chime for calls genuinely not seen before.
  queryClient.invalidateQueries({ queryKey: ['/api/activity-bar'] });
}

interface ProgressSubscription {
  id: string;
  handler: (data: any) => void;
  filter?: {
    type?: string;
    projectId?: string;
  };
}

interface WebSocketContextValue {
  ws: WebSocket | null;
  isConnected: boolean;
  sendTypingIndicator?: (projectId: string, userName: string, isTyping: boolean) => void;
  registerTypingHandler?: (handler: (data: any) => void) => void;
  unregisterTypingHandler?: () => void;
  registerProgressHandler?: (handler: (data: any) => void) => void;
  unregisterProgressHandler?: () => void;
  subscribeToProgress?: (id: string, handler: (data: any) => void, filter?: { type?: string; projectId?: string }) => void;
  unsubscribeFromProgress?: (id: string) => void;
  // V2 Chat Channel System
  registerChannelMessageHandler?: (handler: (data: any) => void) => void;
  unregisterChannelMessageHandler?: () => void;
  registerChannelDeleteHandler?: (handler: (data: any) => void) => void;
  unregisterChannelDeleteHandler?: () => void;
}

const WebSocketContext = createContext<WebSocketContextValue>({ ws: null, isConnected: false });

// Interface for optional TableRenderingManager integration
interface TableRenderingUpdate {
  type: 'entity';
  entityKey: string;
  entityType?: 'pulse' | 'task' | 'sequence';
  payload: any;
  timestamp: number;
}

export function WebSocketProvider({ 
  children, 
  onTableUpdate 
}: { 
  children: React.ReactNode;
  onTableUpdate?: (update: TableRenderingUpdate) => void;
}) {
  const [isConnected, setIsConnected] = useState(false);
  const ws = useRef<WebSocket | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const isAuthenticatedRef = useRef<boolean>(false);
  
  // Exponential backoff for reconnection: 1s → 2s → 4s → 8s → 16s → 30s max
  const reconnectAttemptRef = useRef<number>(0);
  const RECONNECT_BASE_DELAY_MS = 1000;
  const RECONNECT_MAX_DELAY_MS = 30000;
  const typingHandlerRef = useRef<((data: any) => void) | null>(null);
  const progressHandlerRef = useRef<((data: any) => void) | null>(null);
  const progressSubscriptionsRef = useRef<Map<string, ProgressSubscription>>(new Map());
  // V2 Chat Channel System: Separate handler from legacy V1
  const channelMessageHandlerRef = useRef<((data: any) => void) | null>(null);
  const channelDeleteHandlerRef = useRef<((data: any) => void) | null>(null);
  
  const userIdRef = useRef<number | undefined>(undefined);
  useEffect(() => { userIdRef.current = user?.id; }, [user]);

  // True while the socket is (or was) down — cleared after catch-up on open.
  const hadDisconnectRef = useRef<boolean>(false);

  // Visual notification channels (tab-title counter, system notifications)
  // and tab-visibility catch-up: when the user returns to a backgrounded
  // tab, refetch chat data in case throttling or a dropped socket caused
  // missed broadcasts.
  useEffect(() => {
    chatNotify.init();
    const onVisible = () => {
      if (document.hidden) return;
      console.log('[WebSocket] Tab visible — catching up chat queries');
      catchUpChatQueries();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
  
  // INSTANT DELIVERY: No debouncing for maximum 5 users - every event triggers immediately
  // This ensures "plings" and chat bubbles appear without any delay
  const immediateTreeInvalidation = useCallback(() => {
    console.log('[WebSocket] Immediate tree invalidation triggered');
    queryClient.invalidateQueries({ 
      queryKey: ['/api/tree/snapshot'], 
      exact: false 
    });
  }, []);
  
  /**
   * Helper function to invalidate entity-specific detail queries
   * This keeps detail views (pulse dialogs, chat, etc.) in sync
   */
  const invalidateEntityDetails = useCallback((entity: string, invalidation: any, authorUserId?: number) => {
    // NOTE: Tree invalidation is handled upstream in the unified post-delta decision block.
    // This function only invalidates entity-specific detail queries (pulse dialogs, chat, etc.)
    const isOwnEvent = !!(authorUserId && user?.id && authorUserId === user.id);
    
    switch(entity) {
      case 'pulse': {
        const pulseId = invalidation.pulseId || invalidation.meta?.pulseId;
        const projectId = invalidation.projectId || invalidation.meta?.projectId;
        const fields = invalidation.fields as string[] | undefined;
        // Tag/read patches are already applied by the delta manager. Do not
        // refetch pulse HTML, attachments, metadata or the whole table for them.
        if (fields?.length === 1 && fields[0] === 'userTags') break;
        const isCreate = invalidation.type === 'entity.create' || invalidation.type === 'pulse_created';
        
        // Play pulse notification sound for new pulses from other users
        // (this covers pulse notifications since they're created alongside pulses)
        if (isCreate && !isOwnEvent) {
          audioNotifier.playByType('pulse');
        }
        
        // Invalidate individual pulse details
        if (pulseId) {
          queryClient.invalidateQueries({ 
            queryKey: ['/api/pulses', pulseId] 
          });
          
          // Invalidate batch-details containing this pulse
          queryClient.invalidateQueries({
            predicate: (query) => {
              const queryKey = query.queryKey;
              return Array.isArray(queryKey) && 
                queryKey[0] === '/api/pulses/batch-details' &&
                Array.isArray(queryKey[1]) &&
                queryKey[1].includes(pulseId);
            }
          });
          
          // Invalidate batch-metadata when sequences change (for main table badges)
          if (fields?.includes('sequences')) {
            queryClient.invalidateQueries({
              predicate: (query) => {
                const queryKey = query.queryKey;
                return Array.isArray(queryKey) && 
                  queryKey[0] === '/api/pulses/batch-metadata';
              }
            });
          }
          
          // Time tracking queries
          queryClient.invalidateQueries({ 
            queryKey: [`/api/time-tracking/summary/pulse/${pulseId}`] 
          });
          queryClient.invalidateQueries({ 
            queryKey: [`/api/time-tracking/entries/pulse/${pulseId}`] 
          });
        }
        
        // Project-specific pulse queries
        if (projectId) {
          queryClient.invalidateQueries({ 
            queryKey: ['/api/projects', projectId, 'pulses'] 
          });
          queryClient.invalidateQueries({ 
            queryKey: ['/api/projects', projectId, 'pulses', 'paginated'] 
          });
          queryClient.invalidateQueries({ 
            queryKey: ['/api/projects', projectId, 'pulse-progress'] 
          });
        }
        
        // Invalidate notification queries for real-time notification updates
        queryClient.invalidateQueries({ 
          queryKey: ['/api/notifications/count'] 
        });
        queryClient.invalidateQueries({ 
          queryKey: ['/api/notifications'] 
        });
        
        // Update TableRenderingManager if available
        if (onTableUpdate && pulseId) {
          onTableUpdate({
            type: 'entity',
            entityKey: pulseId.toString(),
            entityType: 'pulse',
            payload: invalidation.data || {},
            timestamp: performance.now()
          });
        }
        break;
      }
      
      case 'task': {
        const taskId = invalidation.taskId || invalidation.meta?.taskId || invalidation.id;
        const pulseId = invalidation.pulseId || invalidation.meta?.pulseId;
        const mutationType = invalidation.mutationType || invalidation.meta?.mutationType;
        const taskData = invalidation.data || invalidation.payload;
        
        // SURGICAL UPDATE: Update pulse-workbench cache directly without refetch
        // This keeps the workbench in sync for other users without triggering flicker
        if (pulseId) {
          queryClient.setQueryData(['pulse-workbench', pulseId], (old: any) => {
            if (!old) return old;
            
            if (mutationType === 'create' && taskData) {
              const existingIds = new Set(old.tasks?.map((t: any) => t.id) || []);
              if (!existingIds.has(taskData.id)) {
                return {
                  ...old,
                  tasks: [...(old.tasks || []), taskData],
                };
              }
            } else if (mutationType === 'update' && taskId && taskData) {
              return {
                ...old,
                tasks: (old.tasks || []).map((t: any) =>
                  t.id === taskId ? { ...t, ...taskData } : t
                ),
              };
            } else if (mutationType === 'delete' && taskId) {
              return {
                ...old,
                tasks: (old.tasks || []).filter((t: any) => t.id !== taskId),
              };
            }
            
            return old;
          });
        }
        
        // MINIMAL INVALIDATION: Only invalidate essential non-workbench queries
        // Skip pulse invalidation to prevent flicker - workbench has its own cache
        if (taskId) {
          queryClient.invalidateQueries({ 
            queryKey: ['/api/tasks', taskId] 
          });
        }
        
        // Update TableRenderingManager if available
        if (onTableUpdate && taskId && taskData) {
          onTableUpdate({
            type: 'entity',
            entityKey: taskId.toString(),
            entityType: 'task',
            payload: taskData,
            timestamp: performance.now()
          });
        }
        break;
      }
      
      case 'project': {
        const projectId = invalidation.projectId || invalidation.meta?.projectId;
        
        // Invalidate individual project  
        if (projectId) {
          queryClient.invalidateQueries({ 
            queryKey: ['/api/projects', projectId] 
          });
        }
        break;
      }
      
      case 'sequence': {
        const sequenceId = invalidation.sequenceId || invalidation.meta?.sequenceId;
        const projectId = invalidation.projectId || invalidation.meta?.projectId;
        
        // Invalidate individual sequence
        if (sequenceId) {
          queryClient.invalidateQueries({ 
            queryKey: ['/api/sequences', sequenceId] 
          });
        }
        
        // Project-specific sequence queries
        if (projectId) {
          queryClient.invalidateQueries({ 
            queryKey: ['/api/projects', projectId, 'sequences'] 
          });
          queryClient.invalidateQueries({ 
            queryKey: ['/api/projects', projectId, 'sequences', 'paginated'] 
          });
          queryClient.invalidateQueries({ 
            queryKey: ['/api/projects', projectId] 
          });
        }
        break;
      }
      
      case 'time':
      case 'time_tracking':
      case 'time_entry': {
        // Handle time_tracking_* events
        const pulseId = invalidation.pulseId;
        const projectId = invalidation.projectId;
        
        if (pulseId) {
          queryClient.invalidateQueries({ 
            queryKey: [`/api/time-tracking/entries/pulse/${pulseId}`] 
          });
          queryClient.invalidateQueries({ 
            queryKey: [`/api/time-tracking/summary/pulse/${pulseId}`] 
          });
        }
        
        if (projectId) {
          queryClient.invalidateQueries({ 
            queryKey: [`/api/time-tracking/entries/project/${projectId}`] 
          });
          queryClient.invalidateQueries({ 
            queryKey: [`/api/time-tracking/summary/project/${projectId}`] 
          });
        }
        
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey;
            return Array.isArray(queryKey) && queryKey[0] === '/api/time-tracking/batch';
          }
        });
        break;
      }
      
      case 'progress': {
        // Handle progress_updated - no detail queries to invalidate
        break;
      }
      
      case 'status':
      case 'status_config': {
        // Handle status_config_* events (created/updated/deleted)
        queryClient.invalidateQueries({ 
          queryKey: ['/api/status-configs'] 
        });
        
        const category = invalidation.category;
        if (category) {
          queryClient.invalidateQueries({ 
            queryKey: ['/api/status-configs', category] 
          });
        }
        break;
      }
      
      case 'year': {
        // Handle year_* events (created/updated/deleted)
        queryClient.invalidateQueries({ 
          queryKey: ['/api/years'] 
        });
        
        const yearId = invalidation.id;
        if (yearId) {
          queryClient.invalidateQueries({ 
            queryKey: ['/api/years', yearId] 
          });
        }
        break;
      }
      
      case 'client': {
        // Handle client_* events (created/updated/deleted)
        queryClient.invalidateQueries({ 
          queryKey: ['/api/clients'] 
        });
        
        const clientId = invalidation.id;
        if (clientId) {
          queryClient.invalidateQueries({ 
            queryKey: ['/api/clients', clientId] 
          });
        }
        
        // Invalidate projects since client changes affect project display
        queryClient.invalidateQueries({ 
          queryKey: ['/api/projects'] 
        });
        break;
      }
      
      case 'artist':
      case 'user': {
        queryClient.invalidateQueries({ 
          queryKey: ['/api/artists'] 
        });
        
        queryClient.invalidateQueries({ 
          queryKey: ['/api/users'] 
        });
        
        queryClient.invalidateQueries({ 
          queryKey: ['/api/users/availability-statuses'] 
        });
        
        queryClient.invalidateQueries({ 
          queryKey: ['/api/projects'] 
        });
        break;
      }
      
      // Email accounts (IDLE status, sync time updates)
      case 'email_account': {
        // Invalidate all email account queries for real-time IDLE status & sync time updates
        queryClient.invalidateQueries({ 
          queryKey: ['/api/email-accounts'] 
        });
        
        const accountId = invalidation.id || invalidation.accountId;
        if (accountId) {
          queryClient.invalidateQueries({ 
            queryKey: ['/api/email-accounts', accountId] 
          });
          queryClient.invalidateQueries({ 
            queryKey: ['/api/email-accounts', accountId, 'unassigned-count'] 
          });
        }
        break;
      }
      
      // Email notifications
      case 'email_notification':
      case 'email_notification_created': {
        // Play email match notification sound
        if (invalidation.type === 'email_notification_created'
            || invalidation.type === 'entity.create'
            || (invalidation.mutationType ?? invalidation.meta?.mutationType) === 'create') {
          audioNotifier.playByType('email');
        }
        
        // Invalidate email notification queries when new notifications are created
        queryClient.invalidateQueries({ 
          queryKey: ['/api/email-notifications'] 
        });
        queryClient.invalidateQueries({ 
          queryKey: ['/api/email-notifications/count'] 
        });
        queryClient.invalidateQueries({ 
          queryKey: ['/api/notifications/count'] 
        });
        break;
      }
      
      // Admin notifications
      case 'admin_notification': {
        // Play admin notification sound
        audioNotifier.playByType('admin');
        
        queryClient.invalidateQueries({ 
          queryKey: ['/api/admin-notifications'] 
        });
        queryClient.invalidateQueries({ 
          queryKey: ['/api/admin-notifications/count'] 
        });
        break;
      }
      
      // Security notifications
      case 'security_notification': {
        // Play security notification sound
        audioNotifier.playByType('security');
        
        queryClient.invalidateQueries({ 
          queryKey: ['/api/security-notifications'] 
        });
        queryClient.invalidateQueries({ 
          queryKey: ['/api/security-notifications/count'] 
        });
        break;
      }
      
      // Entities that affect pulses (mapped in RealTimeHub)
      case 'pulse_sequence':
      case 'tag':
      case 'project_time_budget': {
        // These are mapped to parent entities in RealTimeHub
        const projectId = invalidation.projectId;
        if (projectId) {
          queryClient.invalidateQueries({ 
            queryKey: ['/api/projects', projectId] 
          });
        }
        break;
      }
      
      default:
        // Unknown entity type - skip detail invalidation
    }
  }, [onTableUpdate, user?.id]);

  const connect = () => {
    const connectionStartTime = performance.now();
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;
    
    try {
      ws.current = new WebSocket(wsUrl);
      
      ws.current.onopen = () => {
        const connectionDuration = performance.now() - connectionStartTime;
        logger.info('WEBSOCKET', 'WebSocket connected', { connectionDuration: connectionDuration.toFixed(2) });
        setIsConnected(true);

        // Identity-only attachment updates have no replay. Also cover an email
        // opened before the initial socket connection, not just reconnects.
        invalidateEmailWorkbenches(queryClient);
        // Email broadcasts have no replay either. Refresh mounted badge
        // queries and mark closed menus stale on initial connect/reconnect.
        for (const key of [
          '/api/email-notifications',
          '/api/email-notifications/count',
          '/api/email-notifications/unmatched',
          '/api/email-notifications/unmatched/count',
        ]) {
          queryClient.invalidateQueries({ queryKey: [key] });
        }

        // After ANY reconnect, actively catch up on chat data — messages
        // broadcast while the socket was down are otherwise lost until a
        // manual reload (chat has no sequence/replay mechanism).
        if (hadDisconnectRef.current) {
          hadDisconnectRef.current = false;
          console.log('[WebSocket] Reconnected — catching up chat queries');
          catchUpChatQueries();
        }

        // Reset exponential backoff counter on successful connection
        reconnectAttemptRef.current = 0;
        
        // Log successful connection
        performanceMonitor.logWebSocket('connected', connectionDuration, {
          url: wsUrl,
          readyState: ws.current?.readyState,
        });
        
        // Reset auth flag on new connection
        isAuthenticatedRef.current = false;
        
        // Authenticate immediately if user is already available
        sendAuthMessage();
      };
      
      ws.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Diagnostic logging for all inbound WebSocket messages
          console.log('[WebSocket] Raw Inbound:', message.type, message.data?.type || message.data?.entity || '');

          // Payload-free dependency refresh, including our own changes. Never
          // feed this into delta/session echo suppression or infer protected ids.
          if (message.type === 'tree.invalidate') {
            invalidateLinkedShareProjectionQueries(queryClient);
            return;
          }
          
          // Task #130: Side-channel cache invalidation for access-boundary
          // changes (pulse visibility flip, task moved across an admin-only
          // boundary, project access grant/revoke, role change). The payload
          // intentionally carries NO entity data — only scope/scopeId/reason —
          // so we MUST handle it BEFORE the data_invalidation branch (which
          // would otherwise try to feed it to deltaManager.processDelta and
          // expect entity payloads). We just dumb-invalidate the affected
          // React Query keys and let the existing access-checked fetchers
          // resolve the new ground truth.
          if (message.type === 'cache.invalidate') {
            const { scope, scopeId, reason } = message;
            console.log('[WebSocket] cache.invalidate received:', { scope, scopeId, reason });
            if (scope === 'project' && scopeId) {
              queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'], exact: false });
              queryClient.invalidateQueries({ queryKey: ['/api/projects'], exact: false });
              queryClient.invalidateQueries({ queryKey: ['/api/projects', scopeId], exact: false });
              queryClient.invalidateQueries({ queryKey: ['/api/budget-items-bulk'], exact: false });
            } else if (scope === 'pulse' && scopeId) {
              queryClient.invalidateQueries({ queryKey: ['/api/tree/pulse', scopeId], exact: false });
              queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'], exact: false });
            } else if (scope === 'user') {
              queryClient.invalidateQueries({ queryKey: ['/api/notifications'], exact: false });
              queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'], exact: false });
            }
            return;
          }

          if (message.type === 'data_invalidation') {
            const invalidation = message.data;
            const eventType = invalidation.type;
            
            // CRITICAL FIX: Check BOTH locations for authorUserId and sessionId
            // Server may send it at top level OR nested in meta
            const authorId = invalidation.authorUserId ?? invalidation.meta?.authorUserId;
            const sessionId = invalidation.sessionId ?? invalidation.meta?.sessionId;
            const isSelf = !!(authorId && user?.id && authorId === user.id);
            const mutationType = invalidation.mutationType || invalidation.meta?.mutationType;
            const isCreate = mutationType === 'create';

            // Workbench HTML is not part of the tree delta projection. Reconcile
            // email views even when a delta applies or session echo is suppressed.
            // Never merge event payloads into cached content or broaden subscriptions.
            const entity = invalidation.entityType || invalidation.entity || invalidation.meta?.entityType;
            // Notification events are not tree deltas. Handle before delta/
            // self-echo branches, including server events addressed to this user.
            if (entity === 'email_notification' || eventType === 'email_notification_created') {
              invalidateEntityDetails('email_notification', invalidation, authorId);
              return;
            }
            if ((entity === 'pulse' || eventType === 'pulse_updated')
                && (mutationType === 'update' || eventType === 'pulse_updated' || eventType === 'entity.update')) {
              const pulseId = invalidation.pulseId || invalidation.meta?.pulseId
                || invalidation.id || invalidation.entityId || invalidation.meta?.entityId;
              if (typeof pulseId === 'number' && pulseId > 0) {
                invalidateEmailWorkbenches(queryClient, pulseId);
              }
            }
            
            // Check if this is a delta-enabled message with sequence number
            // STRICT VALIDATION: Only process messages with valid sequence numbers
            if (typeof invalidation.sequenceNumber === 'number' && 
                invalidation.sequenceNumber > 0 &&
                invalidation.entityType && 
                invalidation.mutationType) {
              
              try {
                // ALL events flow through processDelta first — no early returns before this
                const result = deltaManager.processDelta({
                  sequenceNumber: invalidation.sequenceNumber,
                  entityType: invalidation.entityType,
                  entityId: invalidation.id || invalidation.pulseId || invalidation.taskId || invalidation.projectId,
                  mutationType: invalidation.mutationType,
                  timestamp: invalidation.timestamp || Date.now(),
                  data: invalidation.data || invalidation,
                  meta: {
                    projectId: invalidation.projectId,
                    pulseId: invalidation.pulseId,
                    taskId: invalidation.taskId,
                    ...invalidation.meta,
                    // RealTimeHub flattens event.meta into live legacy data.
                    // Keep the instruction when converting back to DeltaPayload.
                    treeProjectionChanged: invalidation.treeProjectionChanged === true
                      || invalidation.meta?.treeProjectionChanged === true,
                  }
                });
                
                // SESSION ID ECHO SUPPRESSION for Pulse Workbench (after delta, non-create only)
                // Skip further invalidation if this tab originated the event and delta was applied
                if (!isCreate && result === 'applied' && sessionId && typeof window !== 'undefined') {
                  const checkSession = (window as any).__PULSE_WORKBENCH_CHECK_SESSION__;
                  if (checkSession && checkSession(sessionId)) {
                    console.log('[WebSocket] Session echo suppressed post-delta (applied)');
                    return;
                  }
                }
                
                // UNIFIED POST-DELTA DECISION — single invalidation decision point:
                // deltaFailed = true means cache may be stale, always needs tree + detail refresh
                // deltaApplied + self = no invalidation (optimistic update is correct)
                // deltaApplied + other user = tree invalidation only (delta updated detail cache)
                // create events always trigger tree invalidation (new rows must appear)
                const deltaFailed = result !== 'applied';
                
                if (deltaFailed) {
                  console.log('[WebSocket] Delta not applied:', result, '- full invalidation', { isSelf, entity: invalidation.entityType });
                  immediateTreeInvalidation();
                  const entity = invalidation.entityType;
                  if (entity) {
                    invalidateEntityDetails(entity, invalidation, authorId);
                  }
                } else if (isSelf) {
                  if (isCreate) {
                    console.log('[WebSocket] Self-create delta applied — tree invalidation for new row');
                    immediateTreeInvalidation();
                  } else {
                    console.log('[WebSocket] ✓ Self-event delta applied — no invalidation needed');
                  }
                } else {
                  console.log('[WebSocket] ✓ Other-user delta applied — tree invalidation');
                  immediateTreeInvalidation();
                }
              } catch (error) {
                console.error('[WebSocket] Error processing delta:', error);
                immediateTreeInvalidation();
              }
            } else {
              // Legacy non-delta message
              
              // Parse entity type from event type for detail invalidations
              if (eventType && eventType.includes('_')) {
                let entity: string;
                if (eventType.endsWith('_created')) {
                  entity = eventType.slice(0, -8);
                } else if (eventType.endsWith('_updated')) {
                  entity = eventType.slice(0, -8);
                } else if (eventType.endsWith('_deleted')) {
                  entity = eventType.slice(0, -8);
                } else {
                  [entity] = eventType.split('_');
                }
                
                // Legacy messages: trigger tree invalidation for other users' changes.
                // Self-events on legacy path normally have optimistic updates applied,
                // BUT create events must always invalidate — server-side creation paths
                // (email confirm, call transcription) add rows the client never saw,
                // so suppressing self-creates left the new pulse invisible until reload.
                const isLegacyCreate = isCreate || eventType.endsWith('_created');
                if (!isSelf || isLegacyCreate) {
                  immediateTreeInvalidation();
                }
                invalidateEntityDetails(entity, invalidation, authorId);
              }
            }
          } else if (message.type === 'chat_channel_message') {
            console.log('[WebSocket] V2 chat_channel_message received:', message.channelId);
            if (channelMessageHandlerRef.current) {
              channelMessageHandlerRef.current(message);
            }

            // Task 151: feed missed-call rows addressed to me into the
            // coalescing toast store. The chat unread badge is the
            // primary signal — this is just an additive popup. Wrapped in
            // try/catch so a store error never breaks chat plumbing.
            try {
              const md = message.message?.metadata;
              if (
                md?.type === 'missed_call' &&
                md.missedCall &&
                md.missedCall.calleeId === userIdRef.current &&
                md.missedCall.callerId !== userIdRef.current
              ) {
                const author = message.message?.author;
                const callerName = author
                  ? (author.firstName && author.lastName
                      ? `${author.firstName} ${author.lastName}`
                      : author.username)
                  : `User ${md.missedCall.callerId}`;
                missedCallToastsStore.push({
                  callerId: md.missedCall.callerId,
                  callerName,
                  modality: md.missedCall.modality,
                  channelId: message.channelId,
                });
              }
            } catch (err) {
              console.warn('[WebSocket] missed-call toast push failed:', err);
            }

            queryClient.invalidateQueries({ queryKey: ['/api/chat/channel-unread-counts'] });

            // Visible signal independent of audio: tab-title counter and
            // system notification while the tab is hidden. Fires here (not
            // in ChatDock) so it works even when the dock isn't mounted.
            try {
              if (message.authorId !== userIdRef.current) {
                // Best-effort mute check for topic channels from cache.
                let muted = false;
                if (message.channelType === 'topic') {
                  const topics = queryClient.getQueryData<Array<{ id: string; isMuted?: boolean }>>(
                    ['/api/chat/channels/topics']
                  );
                  muted = !!topics?.find(t => t.id === message.channelId)?.isMuted;
                }
                if (!muted) {
                  // The broadcast payload carries only authorId — resolve
                  // the display name from the cached users list (best effort).
                  let authorName: string | undefined;
                  const cachedUsers = queryClient.getQueryData<Array<{
                    id: number; firstName?: string | null; lastName?: string | null; username?: string;
                  }>>(['/api/users']);
                  const author = cachedUsers?.find(u => u.id === message.authorId);
                  if (author) {
                    authorName = author.firstName && author.lastName
                      ? `${author.firstName} ${author.lastName}`
                      : author.username;
                  }
                  chatNotify.messageReceived({
                    authorName,
                    channelLabel: message.channelType === 'global' ? 'Global'
                      : message.channelType === 'topic' ? 'Topic'
                      : message.channelType === 'client' ? 'Client'
                      : undefined,
                    body: message.message?.content,
                  });
                }
              }
            } catch (err) {
              console.warn('[WebSocket] chat visual notification failed:', err);
            }

            if (message.mentionedUserIds?.includes(userIdRef.current)) {
              console.log('[WebSocket] Current user mentioned in message, invalidating bell badge');
              queryClient.invalidateQueries({ queryKey: ['/api/notifications/count'] });
              queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
              queryClient.invalidateQueries({ queryKey: ['/api/chat/unread-counts-all'] });
            }
          } else if (message.type === 'chat_channel_message_deleted') {
            console.log('[WebSocket] chat_channel_message_deleted received:', message.channelId, message.messageId);
            const { channelId: delChannelId, messageId: delMessageId } = message;
            queryClient.setQueryData<any[]>(
              ['/api/chat/channels', delChannelId, 'messages'],
              (old) => old ? old.filter((m: any) => m.id !== delMessageId) : old
            );
            if (channelDeleteHandlerRef.current) {
              channelDeleteHandlerRef.current(message);
            }
          } else if (message.type === 'channel_member_added') {
            console.log('[WebSocket] channel_member_added received:', message.channel?.id);
            if (message.channel) {
              queryClient.setQueryData(['/api/chat/channels'], (old: any[] | undefined) => {
                if (!old) return [message.channel];
                if (old.some((ch: any) => ch.id === message.channel.id)) return old;
                return [...old, message.channel];
              });
              queryClient.invalidateQueries({ queryKey: ['/api/chat/channels'] });
              queryClient.invalidateQueries({ queryKey: ['/api/chat/channel-unread-counts'] });
            }
          } else if (message.type === 'mention_notification') {
            console.log('[WebSocket] mention_notification received for channel:', message.channelId);
            queryClient.invalidateQueries({
              queryKey: ['/api/notifications/count']
            });
            queryClient.invalidateQueries({
              queryKey: ['/api/chat/unread-counts-all']
            });
            queryClient.invalidateQueries({
              queryKey: ['/api/notifications']
            });
          } else if (message.type === 'chat_reaction_update') {
            // Real-time reaction sync for chat messages
            const { channelId, messageId, reactorId, reactions } = message;
            
            // Echo prevention: ignore events from current user (optimistic UI handles self)
            if (reactorId === user?.id) {
              console.log('[WebSocket] Ignoring own reaction update');
              return;
            }
            
            console.log('[WebSocket] Reaction update received:', { channelId, messageId, action: message.action });
            
            // Update message cache with new reactions
            queryClient.setQueryData(
              ['/api/chat/channels', channelId, 'messages'],
              (oldMessages: any[] | undefined) => {
                if (!oldMessages) return oldMessages;
                return oldMessages.map((msg: any) => 
                  msg.id === messageId 
                    ? { ...msg, reactions } 
                    : msg
                );
              }
            );
            
            // Also update around-message queries (for jump-to-message views)
            queryClient.setQueriesData(
              { predicate: (query) => {
                const key = query.queryKey;
                return Array.isArray(key) && 
                  key[0] === '/api/chat/channels' && 
                  key[1] === channelId && 
                  key[2] === 'messages' && 
                  key[3] === 'around';
              }},
              (oldData: any) => {
                if (!oldData?.messages) return oldData;
                return {
                  ...oldData,
                  messages: oldData.messages.map((msg: any) =>
                    msg.id === messageId 
                      ? { ...msg, reactions } 
                      : msg
                  )
                };
              }
            );
          } else if (message.type === 'entity_reaction_update') {
            const { entityType, entityId, projectId: reactionProjectId, pulseId: reactionPulseId, reactorId: reactionReactorId, reactions: reactionsList } = message;
            
            if (reactionReactorId === user?.id) {
              console.log('[WebSocket] Ignoring own pulse reaction update');
              return;
            }
            
            if (entityType === 'pulse' && reactionsList) {
              const targetPulseId = reactionPulseId || entityId;
              
              queryClient.setQueryData(
                ['/api/reactions', 'pulse', String(targetPulseId)],
                reactionsList
              );
              
              if (reactionProjectId) {
                queryClient.setQueriesData(
                  { predicate: (query) => {
                    const key = query.queryKey;
                    return Array.isArray(key) &&
                      key[0] === '/api/reactions/bulk' &&
                      key[1] === 'pulse' &&
                      key[2] === reactionProjectId;
                  }},
                  (oldData: Record<string, any[]> | undefined) => {
                    if (!oldData) return oldData;
                    return {
                      ...oldData,
                      [String(targetPulseId)]: reactionsList,
                    };
                  }
                );
              } else {
                queryClient.invalidateQueries({ queryKey: ['/api/reactions/bulk', 'pulse'] });
              }
            } else if (entityType === 'pulse') {
              queryClient.invalidateQueries({ queryKey: ['/api/reactions/bulk', 'pulse'] });
              queryClient.invalidateQueries({ queryKey: ['/api/reactions', 'pulse', String(entityId)] });
            }
          } else if (message.type === 'user_typing') {
            // Handle typing indicator
            if (typingHandlerRef.current) {
              typingHandlerRef.current(message.data);
            }
          } else if (message.type === 'pulse_progress_update') {
            // Handle pulse progress updates
            if (progressHandlerRef.current) {
              progressHandlerRef.current(message.data);
            }
          } else if (message.type === 'archive.progress') {
            // Handle archive progress events (ZIP download progress)
            // Dispatch to all matching subscriptions
            progressSubscriptionsRef.current.forEach((subscription) => {
              // Check if subscription filter matches
              const typeMatches = !subscription.filter?.type || subscription.filter.type === message.type;
              const projectMatches = !subscription.filter?.projectId || subscription.filter.projectId === message.projectId;
              
              if (typeMatches && projectMatches) {
                try {
                  subscription.handler(message);
                } catch (error) {
                  console.error('[WebSocket] Error in progress subscription handler:', error);
                }
              }
            });
            
            // Also call legacy handler for backward compatibility
            if (progressHandlerRef.current) {
              progressHandlerRef.current(message);
            }
          } else if (message.type === 'pulse_tags_notification') {
            queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
            queryClient.invalidateQueries({ queryKey: ['/api/notifications/count'] });
            if (message.active) audioNotifier.playByType('pulse');
          } else if (message.type === 'email_notification_actioned') {
            // Remove the notification from every personal notification-list
            // variant. The notification button uses an `unactionedOnly`
            // variant, while other consumers may keep a differently-shaped
            // personal list under the same query-key prefix.
            const { emailAccountId, emailUID } = message.data || message;
            
            queryClient.setQueriesData(
              { queryKey: ['/api/email-notifications'] },
              (oldData: any) => {
                if (!Array.isArray(oldData)) return oldData;
                return oldData.filter((n: any) =>
                  !(n.emailAccountId === emailAccountId && n.emailUID === emailUID)
                );
              },
            );
            
            // Invalidate count and badge queries for instant updates
            queryClient.invalidateQueries({ 
              queryKey: ['/api/email-notifications/count'] 
            });
            queryClient.invalidateQueries({
              queryKey: ['/api/email-notifications/unmatched'],
            });
            queryClient.invalidateQueries({
              queryKey: ['/api/email-notifications/unmatched/count'],
            });
            queryClient.invalidateQueries({ 
              queryKey: ['/api/notifications/count'] 
            });
          } else if (message.type === 'user_activity.update') {
            // Handle user project activity updates (Team Window + ChatDock real-time refresh)
            // Note: userId comes directly on message, not in message.data
            const userId = message.userId;
            if (userId) {
              // Invalidate the specific user's recent-projects query (Team Window)
              queryClient.invalidateQueries({ 
                queryKey: ['/api/users', userId, 'recent-projects'] 
              });
              // Invalidate chat channel queries so ChatDock re-sorts by the updated
              // GREATEST(channel.updatedAt, interaction.lastInteractedAt) timestamp
              queryClient.invalidateQueries({
                queryKey: ['/api/chat/channels']
              });
            }
          } else if (message.type === 'security_notification.created') {
            // Handle real-time security notification push
            // Play security notification sound
            audioNotifier.playByType('security');
            
            // Immediately invalidate security notification queries for instant badge update
            console.log('[WebSocket] Security notification received:', message.data);
            queryClient.invalidateQueries({ 
              queryKey: ['/api/security-notifications/count'] 
            });
            queryClient.invalidateQueries({ 
              queryKey: ['/api/security-notifications'] 
            });
          } else if (message.type === 'project.lifecycle_changed') {
            console.log('[WebSocket] Project lifecycle changed:', message);
            queryClient.invalidateQueries({ 
              queryKey: ['/api/tree/snapshot'], 
              exact: false 
            });
            if (message.projectId) {
              queryClient.invalidateQueries({ 
                queryKey: ['/api/projects', message.projectId] 
              });
            }
          } else if (message.type === 'project.communication_mode_changed') {
            console.log('[WebSocket] Communication mode changed:', message);
            queryClient.invalidateQueries({ 
              queryKey: ['/api/tree/snapshot'], 
              exact: false 
            });
            if (message.projectId) {
              queryClient.invalidateQueries({ 
                queryKey: ['/api/projects', message.projectId] 
              });
              queryClient.invalidateQueries({ 
                queryKey: ['/api/projects', message.projectId, 'pulses'] 
              });
            }
          } else if (typeof message.type === 'string' && message.type.startsWith('time_off.')) {
            // Task #262 — Time Off realtime fan-out. Both the affected user
            // and every connected admin receive `time_off.changed`; the
            // affected user additionally receives `time_off.notification`.
            try {
              queryClient.invalidateQueries({ queryKey: ['/api/time-off/mine'] });
              queryClient.invalidateQueries({ queryKey: ['/api/time-off/all'] });
              queryClient.invalidateQueries({ queryKey: ['/api/time-off/badge'] });
              queryClient.invalidateQueries({ queryKey: ['/api/time-off/notifications'] });
              queryClient.invalidateQueries({ queryKey: ['/api/time-off/usage'] });
              void invalidateTimeOffCalendarSources(queryClient);
              // Company-holiday routes also send a dedicated
              // calendar.invalidate to all internal users. Other time-off
              // changes need this bridge because their legacy event is scoped
              // to the affected user/admin viewers.
              if (message.data?.scope !== 'company' && message.data?.scope !== 'company_holidays') {
                void invalidateCalendar(queryClient);
              }
            } catch (e) {
              console.error('[WebSocket] time_off invalidation failed', e);
            }
          } else if (message.type === 'admin_notification.changed') {
            // Payload-free notification poke used by calendar date-change
            // auditing. The protected endpoints remain authoritative.
            try {
              if (message.silent !== true) audioNotifier.playByType('admin');
              queryClient.invalidateQueries({ queryKey: ['/api/admin-notifications'] });
              queryClient.invalidateQueries({ queryKey: ['/api/admin-notifications/count'] });
            } catch (e) {
              console.error('[WebSocket] admin notification invalidation failed', e);
            }
          } else if (message.type === 'calendar.invalidate') {
            // Calendar services send a payload-free, per-user authorization
            // poke. Refresh only calendar source caches; never refetch the
            // project tree or start a second agenda polling loop.
            try {
              void invalidateCalendar(queryClient);
            } catch (e) {
              console.error('[WebSocket] calendar invalidation failed', e);
            }
          } else if (message.type === 'call:activity') {
            // Payload-free poke: some call started/changed/ended somewhere.
            // Refresh the header activity bar instantly (endpoint re-authorizes
            // per user, so this can't leak anything to unassigned users).
            try {
              queryClient.invalidateQueries({ queryKey: ['/api/activity-bar'] });
            } catch (e) {
              console.error('[WebSocket] activity-bar invalidation failed', e);
            }
          } else if (typeof message.type === 'string' && message.type.startsWith('call:')) {
            // Voice/Video call signalling — fan out to listeners (CallProvider)
            try {
              window.dispatchEvent(new CustomEvent('call:message', { detail: { type: message.type, data: message.data } }));
            } catch (e) {
              console.error('[WebSocket] Error dispatching call event:', e);
            }
          } else if (message.type === 'system.reset') {
            // Handle system-wide database reset (admin deletion)
            // Clear ALL cache and reload the page to ensure clean state
            console.log('[WebSocket] System reset received:', message.data);
            
            // Clear all cached data
            queryClient.clear();
            
            // Show toast notification before reload
            toast({
              title: 'System Reset',
              description: 'The database has been reset. Reloading the application...',
              variant: 'destructive',
            });
            
            // Reload the page after a brief delay to show the toast
            setTimeout(() => {
              window.location.href = '/login';
            }, 1500);
          }
        } catch (error) {
          console.error('[WebSocket] Error parsing message:', error);
        }
      };
      
      ws.current.onclose = () => {
        console.log('[WebSocket] Disconnected');
        setIsConnected(false);
        // Remember the gap so the next successful (re)connect catches up.
        hadDisconnectRef.current = true;
        
        // Only auto-reconnect if this wasn't an intentional close
        if (intentionalCloseRef.current) {
          console.log('[WebSocket] Intentional close - skipping auto-reconnect');
          intentionalCloseRef.current = false; // Reset the flag
          return;
        }
        
        // Exponential backoff reconnection: 1s → 2s → 4s → 8s → 16s → 30s max
        const attempt = reconnectAttemptRef.current;
        const delay = Math.min(
          RECONNECT_BASE_DELAY_MS * Math.pow(2, attempt),
          RECONNECT_MAX_DELAY_MS
        );
        reconnectAttemptRef.current = attempt + 1;
        
        console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${attempt + 1})`);
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      };
      
      ws.current.onerror = (error) => {
        console.error('[WebSocket] Error occurred:', error);
        console.error('[WebSocket] Error details:', {
          type: error.type,
          target: error.target,
          readyState: ws.current?.readyState
        });
      };
    } catch (error) {
      console.error('[WebSocket] Failed to create WebSocket connection:', error);
    }
  };
  
  /**
   * Send authentication message to the WebSocket server
   * Can be called multiple times - will only send once per connection
   */
  const sendAuthMessage = useCallback(() => {
    // Only send if we have a connection, user, and haven't already authenticated
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      console.log('[WebSocket] Not sending auth - connection not open');
      return;
    }
    
    if (!user) {
      console.log('[WebSocket] Not sending auth - user not available');
      return;
    }
    
    if (isAuthenticatedRef.current) {
      console.log('[WebSocket] Already authenticated, skipping');
      return;
    }
    
    try {
      // Auth rides on the httpOnly accessToken cookie sent with the WS
      // upgrade request; the server reads it there. No token in the message.
      const authMessage = {
        type: 'auth',
        data: {}
      };
      ws.current.send(JSON.stringify(authMessage));
      isAuthenticatedRef.current = true;
      console.log(`[WebSocket] ✓ Sent JWT authentication for user ${user.id}`);
    } catch (error) {
      console.error('[WebSocket] Failed to send auth message:', error);
    }
  }, [user]);

  // Initialize DeltaManager on mount
  useEffect(() => {
    deltaManager.initialize(queryClient);
    console.log('[WebSocket] DeltaManager initialized with QueryClient');
    
    return () => {
      // Reset deltaManager on unmount
      deltaManager.reset();
    };
  }, []);

  // Track the last authenticated user ID to detect user changes
  const lastAuthenticatedUserIdRef = useRef<number | null>(null);
  
  // Flag to suppress auto-reconnect when we intentionally close
  const intentionalCloseRef = useRef<boolean>(false);

  // Connect WebSocket ONLY when user is authenticated
  // CRITICAL: Force reconnect when user identity changes
  useEffect(() => {
    // Helper to cleanly close the socket without triggering auto-reconnect
    const closeSocket = () => {
      // Clear any pending reconnect timeout first
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = undefined;
      }
      // Mark as intentional close to prevent onclose from reconnecting
      intentionalCloseRef.current = true;
      if (ws.current) {
        ws.current.close();
        ws.current = null;
      }
      isAuthenticatedRef.current = false;
    };
    
    if (!user) {
      console.log('[WebSocket] Waiting for user authentication before connecting...');
      // If user logs out, close existing connection
      if (ws.current) {
        console.log('[WebSocket] User logged out, closing connection...');
        closeSocket();
        lastAuthenticatedUserIdRef.current = null;
      }
      return;
    }
    
    // Check if user identity changed (different user logged in)
    const userChanged = lastAuthenticatedUserIdRef.current !== null && 
                        lastAuthenticatedUserIdRef.current !== user.id;
    
    if (userChanged && ws.current) {
      console.log(`[WebSocket] User changed from ${lastAuthenticatedUserIdRef.current} to ${user.id}, forcing reconnection...`);
      closeSocket();
    }
    
    // If WebSocket is already open and authenticated for this user, skip
    if (ws.current && ws.current.readyState === WebSocket.OPEN && isAuthenticatedRef.current) {
      console.log('[WebSocket] Already connected and authenticated, skipping reconnect');
      lastAuthenticatedUserIdRef.current = user.id;
      return;
    }
    
    // If WebSocket is already open but NOT authenticated, send auth now
    if (ws.current && ws.current.readyState === WebSocket.OPEN && !isAuthenticatedRef.current) {
      console.log('[WebSocket] Connection open but not authenticated, sending auth message...');
      sendAuthMessage();
      lastAuthenticatedUserIdRef.current = user.id;
      return;
    }
    
    // If WebSocket is still CONNECTING, wait for it to complete
    if (ws.current && ws.current.readyState === WebSocket.CONNECTING) {
      console.log('[WebSocket] Connection in progress, waiting for open...');
      lastAuthenticatedUserIdRef.current = user.id;
      return;
    }
    
    console.log('[WebSocket] User authenticated, connecting WebSocket...');
    // Note: Do NOT reset intentionalCloseRef here - the old socket's onclose may fire after
    // this point and we need the flag to remain true to suppress its auto-reconnect
    connect();
    lastAuthenticatedUserIdRef.current = user.id;
    
    return () => {
      // Cleanup all timeouts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (ws.current) {
        console.log('[WebSocket] Closing connection...');
        intentionalCloseRef.current = true;
        ws.current.close();
        ws.current = null;
      }
    };
  }, [user, sendAuthMessage]);

  useEffect(() => {
    const unsubscribe = onTokenRefresh(() => {
      if (!ws.current || ws.current.readyState === WebSocket.CLOSED || ws.current.readyState === WebSocket.CLOSING) {
        console.log('[WebSocket] Token refreshed — reconnecting WebSocket immediately');
        reconnectAttemptRef.current = 0;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = undefined;
        }
        intentionalCloseRef.current = false;
        connect();
      } else if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        console.log('[WebSocket] Token refreshed — re-authenticating on open socket');
        isAuthenticatedRef.current = false;
        sendAuthMessage();
      }
    });
    return unsubscribe;
  }, [sendAuthMessage]);

  const sendTypingIndicator = useCallback((projectId: string, userName: string, isTyping: boolean) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'typing',
        data: {
          projectId,
          userName,
          isTyping
        }
      }));
    }
  }, []);
  
  const registerTypingHandler = useCallback((handler: (data: any) => void) => {
    typingHandlerRef.current = handler;
  }, []);
  
  const unregisterTypingHandler = useCallback(() => {
    typingHandlerRef.current = null;
  }, []);
  
  const registerProgressHandler = useCallback((handler: (data: any) => void) => {
    progressHandlerRef.current = handler;
  }, []);
  
  const unregisterProgressHandler = useCallback(() => {
    progressHandlerRef.current = null;
  }, []);
  
  const subscribeToProgress = useCallback((
    id: string, 
    handler: (data: any) => void, 
    filter?: { type?: string; projectId?: string }
  ) => {
    progressSubscriptionsRef.current.set(id, { id, handler, filter });
  }, []);
  
  const unsubscribeFromProgress = useCallback((id: string) => {
    progressSubscriptionsRef.current.delete(id);
  }, []);
  
  const registerChannelMessageHandler = useCallback((handler: (data: any) => void) => {
    channelMessageHandlerRef.current = handler;
  }, []);
  
  const unregisterChannelMessageHandler = useCallback(() => {
    channelMessageHandlerRef.current = null;
  }, []);

  const registerChannelDeleteHandler = useCallback((handler: (data: any) => void) => {
    channelDeleteHandlerRef.current = handler;
  }, []);

  const unregisterChannelDeleteHandler = useCallback(() => {
    channelDeleteHandlerRef.current = null;
  }, []);

  return (
    <WebSocketContext.Provider value={{ 
      ws: ws.current,
      isConnected, 
      sendTypingIndicator,
      registerTypingHandler,
      unregisterTypingHandler,
      registerProgressHandler,
      unregisterProgressHandler,
      subscribeToProgress,
      unsubscribeFromProgress,
      registerChannelMessageHandler,
      unregisterChannelMessageHandler,
      registerChannelDeleteHandler,
      unregisterChannelDeleteHandler
    }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export const useWebSocket = () => useContext(WebSocketContext);
